import { css, html, nothing } from 'lit';
import 'src/library/elements/d-section';
import 'src/library/fields/d-view-info';
import 'src/library/editors/elements/d-edit-text';
import 'src/library/editors/elements/d-select-dropdown';
import 'src/library/fields/d-expansion';
import 'src/library/fields/d-spinner-robot';
import { customElement, property, query, state } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { BaseDialog } from 'src/library/components/BaseDialog';
import type { DEditText } from 'src/library/editors/elements/d-edit-text';
import { uuid } from 'src/utilities/text';
import type { NewPartnerDialogResult } from './new-partner-dialog-result';
import type { NewPartnerDialogInput } from './new-partner-dialog-input';
import type { CreatePartnerEventDetail } from './create-partner-event-detail';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';

/**
 *
 * STATUS OK
 */
@customElement('d-new-partner-dialog')
export class DNewPartnerDialog extends BaseDialog<NewPartnerDialogInput, NewPartnerDialogResult> {
  static readonly styles = [
    BaseDialog.styles[0],
    css`
      d-label {
        padding-bottom: 6px;
      }
      .cover {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: white;
        z-index: 1;
      }
    `,
  ];
  @property({ type: String })
  partnerTerm = 'samarbeidspartner';
  @property({ type: String })
  title = '';
  @property({ type: String })
  partnerUuid = '';
  @property({ type: Number })
  width = 400;
  @query('#partnerNameInput')
  partnerNameInput!: DEditText;
  @state()
  private spinner = false;
  @state()
  private newPartnerName = '';

  get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('common:cancel'), action: 'cancel' },
      { name: t('partners.done'), action: 'done', main: true, disabled: this.doneDisabled },
    ];
  }

  protected get calculatedTitle(): string {
    return t('partners.newDialogTitle', { partnerTerm: this.partnerTerm });
  }

  private get doneDisabled() {
    return this.newPartnerName === '';
  }

  renderBody() {
    return html`
      <d-section>
        <d-view-info .content=${`<p>${t('partners.addNewIntro', { partnerTerm: this.partnerTerm })}</p>`}></d-view-info>
        <div>
          <d-edit-text
            id="partnerNameInput"
            placeholder=${t('partners.companyNamePlaceholder')}
            class="fullWidth"
            .value=${this.newPartnerName}
            mark-if-empty
            @value-changed=${(e) => {
              this.newPartnerName = e.detail.value;
            }}
          ></d-edit-text>
        </div>
      </d-section>
      ${this.spinner
        ? html` <div class="cover">
            <d-spinner-robot></d-spinner-robot>
          </div>`
        : nothing}
    `;
  }

  fireCreate() {
    this.dispatchEvent(
      new CustomEvent<CreatePartnerEventDetail>('create-partner', {
        bubbles: true,
        composed: true,
        detail: {
          data: {
            uuid: this.partnerUuid,
            name: this.newPartnerName,
          },
          complete: () => {
            super.onDispatchAction(false, 'done');
          },
        },
      }),
    );
  }

  protected async onDispatchAction(disabled: boolean, action: string) {
    if (!disabled && action === 'done') {
      this.spinner = true;
      this.partnerUuid = uuid();
      this.fireCreate();
    } else {
      await super.onDispatchAction(disabled, action);
    }
  }

  protected fetchResult(detail: string | undefined): NewPartnerDialogResult {
    return {
      action: detail === 'done' ? 'done' : 'cancel',
      partnerUuid: this.partnerUuid,
    };
  }

  protected initializeDialog(input: NewPartnerDialogInput) {
    this.partnerTerm = input.partnerTerm;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-new-partner-dialog': DNewPartnerDialog;
  }
}
